/**
 * Theme as the prototype does it (UX-02, C-3): nothing stored means the
 * system preference decides and dark is the fallback; a stored choice wins;
 * the toggle flips from whatever is showing. Stored per browser until a
 * preferences route exists (D-26).
 */
export const THEME_KEY = "riea-theme";
export type Theme = "dark" | "light";

export function storedTheme(storage: Pick<Storage, "getItem"> | null): Theme | null {
  try {
    const value = storage?.getItem(THEME_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

/** The theme in effect, given the stored choice and whether the system prefers light. */
export function effectiveTheme(stored: Theme | null, systemPrefersLight: boolean): Theme {
  if (stored) return stored;
  return systemPrefersLight ? "light" : "dark";
}

export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  root.setAttribute("data-theme", theme);
}

export function toggleTheme(
  root: HTMLElement = document.documentElement,
  storage: Pick<Storage, "getItem" | "setItem"> | null = safeStorage(),
  systemPrefersLight = window.matchMedia("(prefers-color-scheme: light)").matches,
): Theme {
  const current = root.getAttribute("data-theme");
  const showing: Theme =
    current === "light" || current === "dark" ? current : systemPrefersLight ? "light" : "dark";
  const next: Theme = showing === "dark" ? "light" : "dark";
  applyTheme(next, root);
  try {
    storage?.setItem(THEME_KEY, next);
  } catch {
    // Private windows and blocked storage: the toggle still works for the session.
  }
  return next;
}

export function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
